import React, { useState, useEffect } from 'react';
import { ConfiguratorState } from '../../types';
import { calculateFixedPrice, SKYVISION_FIXED_CONFIG, FIXED_GLASS_OPTIONS, calculateAccessoryPrice } from '../../config/priceConfigFixed';
import { calculateComfortPrice, COMFORT_GLASS_OPTIONS, calculateComfortAccessoryPrice, comfortPricesFromCatalog } from '../../config/priceConfigComfort';
import { calculateWalkOnPrice } from '../../config/priceConfigWalkOn';
import { calculateCircularWalkOnPrice } from '../../config/priceConfigCircularWalkOn';
import { calculateAccessPrice } from '../../config/priceConfigAccess';
import { calculateLinearWalkOnPrice } from '../../config/priceConfigLinearWalkOn';
// Temporär für Tests
import { SERVICE_PRICES, RAL_SURCHARGE } from '../../config/serviceConfig';
import { calculateCircularPrice } from '../../config/priceConfigCircular';
import { calculateLinearPrice } from '../../config/priceConfigLinear';
import { priceService, productCodeFromName } from '../../services/priceService';
import { debug, debugError } from '../../utils/debug';

// Info-Tooltip Komponente
const InfoTooltip = ({ text }: { text: string }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <span className="relative inline-flex items-center ml-2 align-middle">
      <button
        type="button"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={() => setShowTooltip(!showTooltip)}
        className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-skyvision-500 rounded-full hover:bg-skyvision-600 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-skyvision-500 transition-colors"
        style={{ verticalAlign: 'middle' }}
        aria-label="Weitere Informationen"
      >
        i
      </button>
      {showTooltip && (
        <div className="absolute z-50 w-72 p-3 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg shadow-xl bottom-full mb-2 left-0">
          <div className="absolute w-2 h-2 bg-white border-r border-b border-gray-200 transform rotate-45 -bottom-1 left-2"></div>
          <p className="leading-relaxed">{text}</p>
        </div>
      )}
    </span>
  );
};

// Hilfsfunktion: Rundet Durchmesser auf nächst-höheren 100er-Schritt auf (wie Backend)
const roundDiameterToStep = (diameter: number): number => {
  const min = 600;
  const max = 2000;
  const step = 100;
  
  if (diameter < min) return min;
  if (diameter > max) return max;
  
  // Runde AUF zum nächsten 100er-Schritt
  return Math.ceil(diameter / step) * step;
};

interface PriceDisplayProps {
  state?: ConfiguratorState;
  formData?: {
    installation: boolean;
    measurement: boolean;
    technicalDrawing: boolean;
  };
  appliedDiscount?: {
    code: string;
    type: 'percentage' | 'fixed';
    value: number;
    amount: number;
  } | null;
  showGross?: boolean;
}

// Zubehör-Preise und Namen
const ACCESSORIES = {
  'SkyVision FIXED': [
    { id: 'sun_protection_glass', name: 'Sonnenschutzglas', pricePerSquareMeter: 135 },
    { id: 'opal_lamination', name: 'Opallaminierung', pricePerSquareMeter: 110 }
  ],
  'SkyVision COMFORT': [
    { id: 'sun_protection_glass', name: 'Sonnenschutzglas', pricePerSquareMeter: 135 },
    { id: 'opal_lamination', name: 'Opallaminierung', pricePerSquareMeter: 110 },
    { id: 'wcc103', name: 'WCC103 Steuerung', pricePerSquareMeter: 0 }, // Fix-Preis
    { id: 'wcc106', name: 'WCC106 Steuerung', pricePerSquareMeter: 0 }  // Fix-Preis
  ],
  'SkyVision ACCESS': [
    { id: 'sun_protection_glass', name: 'Sonnenschutzglas', pricePerSquareMeter: 135 },
    { id: 'opal_lamination', name: 'Opallaminierung', pricePerSquareMeter: 110 },
    { id: 'wcc106', name: 'WCC106 Steuerung', pricePerSquareMeter: 0 }
  ],
  'SkyVision CIRCULAR': [
    { id: 'sun_protection_glass', name: 'Sonnenschutzglas', pricePerSquareMeter: 135 }
    // Opallaminierung nicht verfügbar für CIRCULAR
  ],
  'SkyVision CIRCULAR WALK-ON': [
    { id: 'sun_protection_glass', name: 'Sonnenschutzglas', pricePerSquareMeter: 135 },
    { id: 'antirutsch_print', name: 'Antirutsch Siebdruck', pricePerSquareMeter: 173 },
    { id: 'antirutsch_structure', name: 'Antirutsch Struktur', pricePerSquareMeter: 225 }
  ],
  'SkyVision WALKON': [
    { id: 'sun_protection_glass', name: 'Sonnenschutzglas', pricePerSquareMeter: 135 },
    { id: 'antirutsch_print', name: 'Antirutsch Siebdruck', pricePerSquareMeter: 173 },
    { id: 'antirutsch_structure', name: 'Antirutsch Struktur', pricePerSquareMeter: 225 }
  ],
  'SkyVision LINEAR': [
    { id: 'sun_protection_glass', name: 'Sonnenschutzglas', pricePerSquareMeter: 135 },
    { id: 'opal_lamination', name: 'Opallaminierung', pricePerSquareMeter: 110 }
  ],
  'SkyVision LINEAR WALK-ON': [
    { id: 'sun_protection_glass', name: 'Sonnenschutzglas', pricePerSquareMeter: 135 },
    { id: 'antirutsch_print', name: 'Antirutsch Siebdruck', pricePerSquareMeter: 173 },
    { id: 'antirutsch_structure', name: 'Antirutsch Struktur', pricePerSquareMeter: 225 }
  ],
};

// Preise pro Modul für LINEAR
// Modulpreise werden jetzt dynamisch über calculateLinearPrice berechnet
const getLinearModulePrice = (moduleType: 'fixed' | 'opening', glassType: 'double' | 'triple' = 'double') => {
  const basePrice = calculateLinearPrice(1, glassType); // 1m² pro Modul
  return moduleType === 'opening' ? basePrice + 800 : basePrice;
};

// Die Preisberechnung erfolgt jetzt über die neue calculateFixedPrice Funktion

// Preisberechnung erfolgt jetzt über die importierten Funktionen

export default function PriceDisplay({ state, formData, appliedDiscount, showGross = true }: PriceDisplayProps) {
  const [apiPrice, setApiPrice] = useState<number | null>(null);
  const [apiBreakdown, setApiBreakdown] = useState<any[]>([]);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  const [useBackendPrice, setUseBackendPrice] = useState(true); // Feature-Flag - BACKEND AKTIVIERT!

  // Wenn kein State vorhanden ist, zeige einen Ladezustand an
  if (!state) {
    return (
      <div className="sticky top-4 bg-white rounded-lg border border-skyvision-200 overflow-hidden p-4">
        <div className="text-center text-skyvision-600">
          Konfiguration wird geladen...
        </div>
      </div>
    );
  }

  // Backend-Preis laden wenn Feature aktiviert und sich State ändert
  useEffect(() => {
    if (!useBackendPrice || !state.product || !state.glassType) {
      setApiPrice(null);
      setApiBreakdown([]); // Auch Breakdown zurücksetzen
      return;
    }

    const loadBackendPrice = async () => {
      setIsLoadingPrice(true);
      try {
        // Produkt-Code konvertieren
        const productCode = productCodeFromName(state.product) || 'skyvision-fixed';

        // Dimensionen vorbereiten
        const dimensions: any = {
          width: state.dimensions.width || 250,
          length: state.dimensions.length || 250,
          height: state.dimensions.height || 150
        };

        // CIRCULAR: Durchmesser hinzufügen (auf 100er-Schritt aufgerundet)
        if (state.product.toLowerCase().includes('circular')) {
          const inputDiameter = state.dimensions.diameter || 600;
          const roundedDiameter = roundDiameterToStep(inputDiameter);
          dimensions.diameter = roundedDiameter;
          
          debug(`📐 CIRCULAR Durchmesser: ${inputDiameter}mm → ${roundedDiameter}mm (auf 100er aufgerundet)`);
        }

        // Debug-Log
        debug('🔍 Frontend: API Request wird gesendet:', {
          product: productCode,
          dimensions,
          glassType: state.glassType,
          accessories: state.accessories || [],
          ralColorOutside: state.ralColor,
          ralColorInside: state.ralColorInside,
          controlType: state.controlType
        });
        
        debug('🔍 Frontend: State.accessories im Detail:', state.accessories);
        debug('🔍 Frontend: Anzahl Accessories:', (state.accessories || []).length);

        const result = await priceService.calculatePrice({
          product: productCode,
          dimensions,
          glassType: (state.glassType as 'double' | 'triple' | 'none') || 'double',
          accessories: state.accessories || [], // Accessories ans Backend senden für korrekte Berechnung
          ralColorOutside: state.ralColor,
          ralColorInside: state.ralColorInside,
          controlType: state.controlType
        });

        // Prüfe ob Backend einen Preis zurückgibt
        debug('✅ Backend Antwort erhalten:', result);
        
        // Backend-Preis IMMER verwenden (auch wenn 0€, dann ist es eben 0€!)
        setApiPrice(result.total_price);
        setApiBreakdown(result.breakdown || []);
        
        if (result.total_price === 0) {
          console.warn('⚠️ Backend liefert Preis 0€ - Prüfe ob Preise in der DB sind!');
        }
      } catch (error) {
        debugError('Fehler beim Laden des Backend-Preises:', error);
        setApiPrice(null);
        setApiBreakdown([]); // Auch Breakdown zurücksetzen bei Fehler
      } finally {
        setIsLoadingPrice(false);
      }
    };

    loadBackendPrice();
  }, [useBackendPrice, state.product, state.dimensions, state.glassType, state.accessories, state.ralColor, state.ralColorInside, state.controlType]);

  // Berechne den Grundpreis basierend auf dem Produkt und den Maßen
  const getBasePrice = () => {
    if (state.product === 'SkyVision FIXED') {
      // Neue Preisberechnung für FIXED mit 25€ pro 10mm Schritt
      const glassType = state.glassType as 'double' | 'triple' || 'double';
      return calculateFixedPrice(
        state.dimensions.width,
        state.dimensions.length,
        state.dimensions.height,
        glassType
      );
    } else if (state.product === 'SkyVision COMFORT') {
      const glassType = state.glassType as 'double' | 'triple' || 'double';
      const result = calculateComfortPrice(
        state.dimensions.width,
        state.dimensions.length,
        state.dimensions.height,
        glassType,
        state.controlType,
        comfortPricesFromCatalog(
          priceService.peekAccessories(productCodeFromName(state.product)) || undefined,
        ),
      );
      return result.totalPrice;
    } else if (state.product === 'SkyVision ACCESS') {
      const glassType = (state.glassType as 'double' | 'triple') || 'double';
      const result = calculateAccessPrice(
        state.dimensions.width,
        state.dimensions.length,
        state.dimensions.height,
        glassType,
        state.controlType ?? 'wcc106',
        comfortPricesFromCatalog(
          priceService.peekAccessories(productCodeFromName(state.product)) || undefined,
        ),
      );
      return result.totalPrice;
    } else if (state.product === 'SkyVision WALKON') {
      const glassType = (state.glassType as 'double' | 'triple' | 'none') || 'double';
      return calculateWalkOnPrice(
        state.dimensions.width,
        state.dimensions.length,
        glassType,
      ).totalPrice;
    } else if (state.product === 'SkyVision CIRCULAR WALK-ON') {
      const glassType = (state.glassType as 'double' | 'triple' | 'none') || 'double';
      return calculateCircularWalkOnPrice(
        state.dimensions.diameter || 600,
        glassType,
      ).totalPrice;
    } else if (state.product === 'SkyVision LINEAR WALK-ON') {
      const glassType = (state.glassType as 'double' | 'triple' | 'none') || 'double';
      return calculateLinearWalkOnPrice(
        state.dimensions.width,
        state.dimensions.length,
        glassType,
      );
    } else if (state.product === 'SkyVision LINEAR') {
      // Berechne den Preis basierend auf der Anzahl und Art der Module
      const fixedModules = state.modules?.fixed || 0;
      const openingModules = state.modules?.opening || 0;
      const glassType = state.glassType as 'double' | 'triple' || 'double';
      return (fixedModules * getLinearModulePrice('fixed', glassType)) + (openingModules * getLinearModulePrice('opening', glassType));
    } else     if (state.product === 'SkyVision CIRCULAR') {
      const inputDiameter = state.dimensions.diameter || 0;
      const roundedDiameter = roundDiameterToStep(inputDiameter);
      const glassType = state.glassType as 'double' | 'triple' || 'double';
      return calculateCircularPrice(roundedDiameter, glassType);
    }
    return 0;
  };

  const formatPrice = (price: number) => {
    const grossPrice = price * 1.19;
    return (showGross ? grossPrice : price).toLocaleString('de-DE', {
      style: 'currency',
      currency: 'EUR'
    });
  };

  const formatGlassType = () => {
    if (!state.glassType) return 'Keine Verglasung ausgewählt';
    
    // Verwende den Namen aus glazingInfo falls vorhanden
    if (state.glazingInfo?.name) {
      return state.glazingInfo.name;
    }
    
    // Fallback auf Standard-Namen
    switch (state.glassType) {
      case 'double':
        return '2-fach Isolierverglasung';
      case 'triple':
        return '3-fach Isolierverglasung';
      case 'none':
        return 'Begehbar ohne Isolierglas';
      default:
        return 'Keine Verglasung ausgewählt';
    }
  };
  
  const formatGlazingSurcharge = () => {
    if (!state.glazingInfo || !state.glassType) {
      return null;
    }
    
    const info = state.glazingInfo;
    
    switch (info.surchargeType) {
      case 'percentage':
        return info.surchargePercentage ? `(+${info.surchargePercentage}% Aufschlag)` : null;
      case 'fixed':
        return info.surchargeFixed ? `(+${info.surchargeFixed.toFixed(2)}€ Aufschlag)` : null;
      case 'formula':
        return '(+Aufpreis für größeren Aufsatzkranz)';
      case 'none':
      default:
        return null;
    }
  };

  // Bereinigt Backend-Texte für bessere Lesbarkeit und gibt optional Erklärungstext zurück
  const formatBreakdownItemName = (itemName: string): { name: string; explanation?: string } => {
    // Ersetze "double-fach" mit "2-fach"
    let formatted = itemName.replace(/double-fach/gi, '2-fach');
    // Ersetze "triple-fach" mit "3-fach"
    formatted = formatted.replace(/triple-fach/gi, '3-fach');
    // Ersetze "double" mit "2-fach" (falls ohne "-fach")
    formatted = formatted.replace(/\bdouble\b/gi, '2-fach');
    // Ersetze "triple" mit "3-fach" (falls ohne "-fach")
    formatted = formatted.replace(/\btriple\b/gi, '3-fach');
    // Ersetze "UK" / "Unterkonstruktion" mit "Aufsatzkranz"
    formatted = formatted.replace(/Unterkonstruktion/g, 'Aufsatzkranz');
    formatted = formatted.replace(/\bUK\b/g, 'Aufsatzkranz');
    formatted = formatted.replace(/UK-/g, 'Aufsatzkranz-');
    
    // Für COMFORT: Ersetze "FIXED Grundpreis" mit "COMFORT Grundpreis"
    if (
      (state.product === 'SkyVision COMFORT' || state.product === 'SkyVision ACCESS') &&
      formatted.includes('FIXED Grundpreis')
    ) {
      formatted = formatted.replace(
        'FIXED Grundpreis',
        state.product === 'SkyVision ACCESS' ? 'ACCESS Grundpreis' : 'COMFORT Grundpreis',
      );
    }
    
    // Bestimme Erklärungstext basierend auf dem Item-Typ
    let explanation: string | undefined;
    
    // Aufsatzkranz-Mehrpreis
    if (formatted.toLowerCase().includes('aufsatzkranz') && formatted.toLowerCase().includes('mehrpreis')) {
      explanation = 'Bei einer 3-fach Verglasung benötigen wir einen größeren und stabileren Aufsatzkranz, um das höhere Gewicht und die größere Dicke des Glases zu tragen. Dies führt zu einem Mehrpreis.';
    }
    // Verglasungsaufschlag
    else if (formatted.toLowerCase().includes('verglasungsaufschlag') || formatted.toLowerCase().includes('verglasung')) {
      if (formatted.includes('3-fach')) {
        explanation = 'Die 3-fach Verglasung bietet bessere Wärmedämmung und Schallschutz, ist jedoch aufwendiger in der Herstellung und schwerer als die 2-fach Variante.';
      }
    }
    // Sonnenschutzglas
    else if (formatted.toLowerCase().includes('sonnenschutz')) {
      explanation = 'Das Sonnenschutzglas reduziert die Wärmeentwicklung im Raum durch Sonneneinstrahlung und schützt vor UV-Strahlung. Die spezielle Beschichtung führt zu einem Mehrpreis.';
    }
    // Opallaminierung
    else if (formatted.toLowerCase().includes('opal')) {
      explanation = 'Die Opallaminierung sorgt für eine milchige, lichtdurchlässige Optik und bietet Sichtschutz. Die zusätzliche Beschichtung führt zu einem Mehrpreis.';
    }
    // RAL-Farben
    else if (formatted.toLowerCase().includes('ral') && !formatted.includes('7043') && !formatted.includes('9010')) {
      explanation = 'Individuelle RAL-Farben erfordern eine spezielle Pulverbeschichtung in der gewünschten Farbe, was zu einem Mehrpreis gegenüber den Standardfarben führt.';
    }
    
    return { name: formatted, explanation };
  };

  const formatDimensions = () => {
    if (!state.dimensions) return 'Keine Maße ausgewählt';
    
    if (state.product === 'SkyVision CIRCULAR') {
      return `Durchmesser: ${state.dimensions.diameter || 0} mm`;
    } else if (state.product === 'SkyVision LINEAR WALK-ON') {
      const modules = state.modules?.fixed || 1;
      return `${modules} Module · ${state.dimensions.width || 0} × ${state.dimensions.length || 0} mm`;
    } else if (state.product === 'SkyVision LINEAR') {
      const fixedModules = state.modules?.fixed || 0;
      const openingModules = state.modules?.opening || 0;
      return `${fixedModules + openingModules} Module (${fixedModules} fest, ${openingModules} öffenbar)
              Höhe: ${state.dimensions.height || 0} mm`;
    } else {
      return `${state.dimensions.width || 0} × ${state.dimensions.length || 0} × ${state.dimensions.height || 0} mm`;
    }
  };

  // Berechne die Fläche für flächenbasierte Zubehör-Preise
  const calculateArea = () => {
    if (state.product === 'SkyVision CIRCULAR') {
      const inputDiameter = state.dimensions.diameter || 600;
      const roundedDiameter = roundDiameterToStep(inputDiameter);
      // Glasfläche = Durchmesser + 260mm (130mm Überstand je Seite)
      const glassDiameter = (roundedDiameter + 260) / 1000; // in Meter
      const radius = glassDiameter / 2;
      return Math.PI * radius * radius; // πr² in m²
    } else {
      const width = state.dimensions.width || 250;
      const length = state.dimensions.length || 250;
      // Glasfläche = Bestellmaß + 260mm (130mm je Seite)
      const glassWidth = (width + 260) / 1000; // in Meter
      const glassLength = (length + 260) / 1000; // in Meter
      return glassWidth * glassLength; // Fläche in m²
    }
  };

  // Berechne Accessory-Preis für CIRCULAR
  const calculateCircularAccessoryPrice = (accessoryId: string) => {
    const { CIRCULAR_GLASS_OPTIONS } = require('../../config/priceConfigCircular');
    const option = CIRCULAR_GLASS_OPTIONS[accessoryId];
    if (!option) return 0;
    
    const inputDiameter = state.dimensions.diameter || 600;
    const roundedDiameter = roundDiameterToStep(inputDiameter);
    const area = option.calculateArea(roundedDiameter);
    const calculatedPrice = option.pricePerSqm * area;
    
    // Minimum-Preis wenn unter 1m²
    return area < 1.0 ? option.minPrice : Math.round(calculatedPrice);
  };

  // Berechne den Gesamtpreis (nur für lokale Berechnung ohne Backend)
  const calculateTotalPrice = () => {
    let totalPrice = getBasePrice(); // Beinhaltet bereits Grundpreis + Maße + Verglasung

    // Addiere Preise für Zubehör
    if (state.accessories && state.accessories.length > 0) {
      state.accessories.forEach(id => {
        // Für CIRCULAR: Spezielle Berechnung
        if (state.product === 'SkyVision CIRCULAR') {
          const accessoryPrice = calculateCircularAccessoryPrice(id);
          totalPrice += accessoryPrice;
          debug(`  + CIRCULAR Accessory ${id}: ${accessoryPrice}€`);
        } else {
          // Für FIXED, COMFORT, LINEAR: Normale Berechnung
          const area = calculateArea();
          const accessoryList = ACCESSORIES[state.product as keyof typeof ACCESSORIES] || [];
          const accessory = accessoryList.find(acc => acc.id === id);
          if (accessory) {
            const accessoryPrice = accessory.pricePerSquareMeter 
              ? Math.round(accessory.pricePerSquareMeter * area)
              : (accessory.price || 0);
            totalPrice += accessoryPrice;
            debug(`  + Accessory ${id}: ${accessoryPrice}€`);
          }
        }
      });
    }

    // Addiere Preise für Services - Montage und Aufmaß entfernt, Technische Zeichnung ist inklusiv
    // (Keine zusätzlichen Service-Kosten mehr)

    // RAL-Farben werden NICHT hier addiert, wenn Backend-Preis verwendet wird!
    // Sie werden in calculateFinalPrice() hinzugefügt (nur wenn Backend aktiv ist)
    if (!useBackendPrice) {
      // Addiere Preis für RAL-Farbe außen (nur wenn nicht Standard)
      if (state.ralColor && state.ralColor !== 'RAL 7043') {
        totalPrice += RAL_SURCHARGE;
      }

      // Addiere Preis für RAL-Farbe innen (nur wenn nicht Standard)
      if (state.ralColorInside && state.ralColorInside !== 'RAL 9010') {
        totalPrice += RAL_SURCHARGE;
      }
    }

    return totalPrice;
  };

  // Berechnet den Netto-Gesamtpreis (inkl. RAL-Farben und Accessories)
  const getTotalNettoPrice = () => {
    if (apiPrice === null) return 0;
    return apiPrice;
  };

  // Rabattbetrag dynamisch neu berechnen
  const calculateCurrentDiscountAmount = () => {
    if (!appliedDiscount) return 0;
    
    const currentNettoPrice = getTotalNettoPrice();
    
    if (appliedDiscount.type === 'percentage') {
      return (currentNettoPrice * appliedDiscount.value) / 100;
    } else {
      return appliedDiscount.value; // Festbetrag bleibt gleich
    }
  };

  // Endpreis mit aktuell berechnetem Rabatt
  const calculateFinalPrice = () => {
    const nettoPrice = getTotalNettoPrice();
    const currentDiscountAmount = calculateCurrentDiscountAmount();
    const finalPrice = nettoPrice - currentDiscountAmount;
    
    // Wenn Preis 0 oder negativ, ist die Konfiguration ungültig
    return finalPrice > 0 ? finalPrice : 0;
  };
  
  // Prüfe ob aktuelle Konfiguration gültig ist
  const isValidConfiguration = () => {
    // Wenn noch kein Produkt oder keine Verglasung ausgewählt wurde, ist die Konfiguration noch nicht vollständig
    // In diesem Fall keine Fehlermeldung anzeigen
    if (!state.product || !state.glassType) {
      return true; // Als "gültig" behandeln, damit keine Fehlermeldung erscheint
    }
    
    // Wenn Produkt und Verglasung ausgewählt sind, prüfe ob ein Preis vorhanden ist
    const price = calculateFinalPrice();
    return price > 0;
  };

  return (
    <div className="sticky top-4 bg-white rounded-lg border-2 border-dashed border-skyvision-300 overflow-hidden">
      <div className="bg-gradient-to-r from-skyvision-50 to-skyvision-100 px-4 py-3 border-b-2 border-dashed border-skyvision-300">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-skyvision-900 flex items-center">
            <svg className="w-5 h-5 mr-2 text-skyvision-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Ihre Konfiguration
          </h3>
          {/* Feature-Flag Toggle (nur für Entwicklung) */}
          {process.env.NODE_ENV === 'development' && (
            <button
              type="button"
              onClick={() => setUseBackendPrice(!useBackendPrice)}
              className={`text-xs px-2 py-1 rounded ${
                useBackendPrice ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
              }`}
              title="Backend-API für Preise verwenden"
            >
              {useBackendPrice ? '🔌 API' : '💾 Lokal'}
            </button>
          )}
        </div>
        {isLoadingPrice && (
          <div className="text-xs text-skyvision-500 mt-1">
            Preis wird vom Backend geladen...
          </div>
        )}
      </div>
      <div className="p-5">
        {/* Basis Konfiguration Überschrift */}
        <div className="mb-4">
          <h4 className="text-base font-bold text-skyvision-900 uppercase tracking-wide">Basis Konfiguration</h4>
        </div>

        {/* Produkt - wird immer angezeigt wenn ausgewählt */}
        {state.product && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5 text-skyvision-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <h4 className="text-sm font-semibold text-gray-900">Produkt</h4>
            </div>
            <p className="text-base font-medium text-skyvision-900 ml-7">{state.product}</p>
          </div>
        )}

        {/* Verglasung - nur anzeigen wenn ausgewählt */}
        {state.glassType && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5 text-skyvision-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h4 className="text-sm font-semibold text-gray-900">Verglasung</h4>
            </div>
            <div className="ml-7">
              <p className="text-base font-medium text-skyvision-900">{formatGlassType()}</p>
              {formatGlazingSurcharge() && (
                <p className="text-xs text-skyvision-500 mt-1">{formatGlazingSurcharge()}</p>
              )}
            </div>
          </div>
        )}

        {/* Maße - nur anzeigen wenn konfiguriert (nicht 0) */}
        {(state.dimensions.width > 0 || state.dimensions.diameter > 0) && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5 text-skyvision-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
              <h4 className="text-sm font-semibold text-gray-900">Maße (Bestellmaße)</h4>
            </div>
            <p className="text-base font-medium text-skyvision-900 ml-7">{formatDimensions()}</p>
          </div>
        )}

        {/* RAL-Farbe Außen - nur anzeigen wenn NICHT Standard (RAL 7043) */}
        {state.ralColor && state.ralColor !== 'RAL 7043' && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5 text-skyvision-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
              <h4 className="text-sm font-semibold text-gray-900">RAL-Farbe (Außen)</h4>
            </div>
            <div className="ml-7">
              <p className="text-base font-medium text-skyvision-900">{state.ralColor}</p>
            </div>
          </div>
        )}

        {/* RAL-Farbe Innen - nur anzeigen wenn NICHT Standard (RAL 9010) */}
        {state.ralColorInside && state.ralColorInside !== 'RAL 9010' && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5 text-skyvision-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
              <h4 className="text-sm font-semibold text-gray-900">RAL-Farbe (Innen)</h4>
            </div>
            <div className="ml-7">
              <p className="text-base font-medium text-skyvision-900">{state.ralColorInside}</p>
            </div>
          </div>
        )}

        {/* Zubehör - nur anzeigen wenn ausgewählt */}
        {state.accessories && state.accessories.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5 text-skyvision-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <h4 className="text-sm font-semibold text-gray-900">Zubehör</h4>
            </div>
            <div className="ml-7 space-y-2">
              {state.accessories.map((accessoryId, index) => {
                const catalog = priceService.peekAccessories(productCodeFromName(state.product)) || [];
                const accessory = catalog.find(
                  (acc) => acc.accessory_code === accessoryId || acc.id === accessoryId,
                );
                const fallbackList = ACCESSORIES[state.product as keyof typeof ACCESSORIES] || [];
                const fallback = fallbackList.find((acc) => acc.id === accessoryId);
                return (
                  <p key={index} className="text-sm text-gray-900">
                    • {accessory?.name || fallback?.name || accessoryId}
                  </p>
                );
              })}
            </div>
          </div>
        )}

        {/* Trennlinie - nur anzeigen wenn mindestens ein Wert konfiguriert ist */}
        {(state.product || state.glassType || state.dimensions.width > 0 || (state.ralColor && state.ralColor !== 'RAL 7043') || (state.ralColorInside && state.ralColorInside !== 'RAL 9010') || (state.accessories && state.accessories.length > 0)) && (
          <div className="border-t-2 border-dashed border-skyvision-200 my-5"></div>
        )}

        {/* Preisaufschlüsselung - Verwende Backend-Breakdown wenn verfügbar */}
        {useBackendPrice && apiBreakdown && apiBreakdown.length > 0 && (
          <div className="mb-5">
            <div className="mb-4">
              <h4 className="text-base font-bold text-skyvision-900 uppercase tracking-wide">Preisaufschlüsselung</h4>
            </div>
            <div className="space-y-3">
              {apiBreakdown.map((item, index) => {
                // Bereinige den Item-Namen für bessere Lesbarkeit und hole Erklärung
                const { name: formattedItemName, explanation } = formatBreakdownItemName(item.item);
                
                // Erstelle Beschreibungstext basierend auf Typ
                let description = '';
                if (item.type === 'fixed') {
                  description = 'Festpreis';
                } else if (item.type === 'percentage') {
                  description = 'Prozentual vom Grundpreis';
                } else if (item.type === 'per_unit') {
                  description = 'Pro Einheit';
                }
                
                return (
                  <div key={index} className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-900 inline-flex items-center">
                          {formattedItemName}
                          {explanation && <InfoTooltip text={explanation} />}
                        </span>
                      </div>
                      {description && (
                        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                      )}
                    </div>
                    <span className="text-sm font-semibold text-skyvision-600 ml-4 flex-shrink-0">
                      {formatPrice(item.price)}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="border-t-2 border-dashed border-skyvision-200 mt-5"></div>
          </div>
        )}

        {/* Inklusive Services */}
        {formData && formData.technicalDrawing && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h4 className="text-sm font-semibold text-green-800">Inklusive Leistungen</h4>
            </div>
            <div className="ml-7 bg-green-50 rounded-lg p-3">
              <p className="text-sm font-medium text-green-800">✓ Technische Zeichnung</p>
            </div>
            <div className="border-t-2 border-dashed border-skyvision-200 mt-5"></div>
          </div>
        )}

        {/* Rabatt-Anzeige */}
        {appliedDiscount && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <h4 className="text-sm font-semibold text-green-800">Rabatt angewendet</h4>
            </div>
            <div className="ml-7 bg-green-50 rounded-lg p-3">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-green-800">Code: {appliedDiscount.code}</p>
                  <p className="text-xs text-green-600 mt-0.5">
                    {appliedDiscount.type === 'percentage' 
                      ? `${appliedDiscount.value}% Rabatt` 
                      : `${appliedDiscount.value.toLocaleString('de-DE')}€ Festrabatt`
                    }
                  </p>
                </div>
                <span className="text-base font-bold text-green-600">
                  -{formatPrice(calculateCurrentDiscountAmount())}
                </span>
              </div>
            </div>
            <div className="border-t-2 border-dashed border-skyvision-200 mt-5"></div>
          </div>
        )}

        {/* Gesamtpreis */}
        <div className="bg-gradient-to-br from-skyvision-600 to-skyvision-700 rounded-xl p-5 shadow-lg">
          {!isValidConfiguration() && (
            <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200 text-red-700">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <strong className="text-sm font-semibold">Diese Konfiguration ist nicht verfügbar</strong>
                  <p className="text-xs mt-1">
                    Für diese Abmessungen ist leider kein Preis hinterlegt. 
                    Bitte wählen Sie andere Dimensionen oder kontaktieren Sie uns für ein individuelles Angebot.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex justify-between items-baseline mb-3">
            <span className="text-white text-lg font-semibold">
              Gesamtpreis
            </span>
          </div>
          
          <div className="bg-white rounded-lg p-4">
            <div className={`text-3xl font-bold text-center ${isValidConfiguration() ? 'text-skyvision-700' : 'text-gray-400'}`}>
              {!state.glassType ? (
                <span className="text-base font-normal text-gray-500">Bitte Verglasung wählen</span>
              ) : isValidConfiguration() ? (
                formatPrice(calculateFinalPrice())
              ) : (
                'Nicht verfügbar'
              )}
            </div>
            {appliedDiscount && isValidConfiguration() && (
              <div className="flex justify-center items-center gap-2 text-sm text-gray-500 mt-2">
                <span>Ursprünglich:</span>
                <span className="line-through">{formatPrice(calculateTotalPrice())}</span>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2 text-center">
              {showGross ? 'Bruttopreis inkl. 19% MwSt.' : 'Nettopreis zzgl. 19% MwSt.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 